import { useEffect, useRef } from 'react';
import {
  Button,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { MODAL_FOOTER_PB, MODAL_HEADER_PT } from '@/constants/mobileLayout';
import { useMobileFormModal } from '@/hooks/useMobileFormModal';

/**
 * /profile Support CTA.
 *
 * Layout: [Never][Not now] …… [Support 10 Teams]
 * Matte traffic-light colours (dusty red / ochre / sage) with soft hover fills.
 *
 * Calls `onPresented` when `isOpen` becomes true (useEffect after commit).
 * Initial focus goes to the primary CTA so Never is not auto-highlighted.
 */
export function SupportPlansModal(props: {
  isOpen: boolean;
  isNeverBusy?: boolean;
  onPresented?: () => void;
  onSupport: () => void;
  onNotNow: () => void;
  onNever: () => void;
}) {
  const { isOpen, isNeverBusy = false, onPresented, onSupport, onNotNow, onNever } = props;
  const { modalSize, isCentered, scrollBehavior, contentProps, bodyProps, footerPb } = useMobileFormModal();
  const supportButtonRef = useRef<HTMLButtonElement>(null);

  // Matte traffic lights — lower saturation so they sit quietly on the modal
  // Red — stop / reject
  const neverBg = useColorModeValue('#B65C5C', '#9E5C5C');
  const neverBgHover = useColorModeValue('#A34F4F', '#8A5050');
  const neverBorder = useColorModeValue('#8F4545', '#7A4646');
  const neverColor = 'white';

  // Amber — pause / defer
  const notNowBg = useColorModeValue('#C4A24A', '#A89050');
  const notNowBgHover = useColorModeValue('#B0903E', '#968044');
  const notNowBorder = useColorModeValue('#957A35', '#847038');
  const notNowColor = useColorModeValue('#1C1917', '#1C1917');

  // Green — go / primary
  const supportBg = useColorModeValue('#4F8F6A', '#4D7F64');
  const supportBgHover = useColorModeValue('#427A5A', '#426E56');
  const supportBorder = useColorModeValue('#36664A', '#385F4A');
  const supportColor = 'white';

  useEffect(() => {
    if (isOpen) {
      onPresented?.();
    }
  }, [isOpen, onPresented]);

  const sharedButtonProps = {
    borderWidth: '2px',
    fontWeight: 700,
    px: 4,
  } as const;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onNotNow}
      isCentered={isCentered}
      size={modalSize}
      scrollBehavior={scrollBehavior}
      closeOnOverlayClick={false}
      initialFocusRef={supportButtonRef}
    >
      <ModalOverlay />
      <ModalContent {...contentProps} maxW={{ md: '560px' }}>
        <ModalHeader pt={MODAL_HEADER_PT} pb={2} flexShrink={0}>
          <Heading fontSize={{ base: 'xl', md: '2xl' }} color='chakra-heading-text'>
            Our Plans for the Future
          </Heading>
        </ModalHeader>
        <ModalBody {...bodyProps} px={{ base: 3, md: 6 }}>
          <VStack spacing={4} align='stretch'>
            <Text color='chakra-body-text'>
              10 Teams will stay free. No charge to play, no charge to host leagues, and no ads. Many of you
              said in the survey you&apos;d pay to play or host a league — thank you — but we won&apos;t charge
              for either. To sustain the product, we aim to secure a sponsor for the website, the upcoming app,
              and the newsletter before the Women&apos;s World Cup 2027.
            </Text>
            <Text color='chakra-body-text'>
              If you enjoyed playing during this World Cup and want to help in the meantime, consider supporting
              10 Teams. Any contribution helps keep the game free and is enormously appreciated!
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter pb={footerPb ?? MODAL_FOOTER_PB} flexShrink={0}>
          <Flex
            w='full'
            direction={{ base: 'column', sm: 'row' }}
            align={{ base: 'stretch', sm: 'center' }}
            justify='space-between'
            gap={3}
          >
            <HStack spacing={3} align='stretch' w={{ base: 'full', sm: 'auto' }}>
              <Button
                {...sharedButtonProps}
                flex={{ base: 1, sm: 'initial' }}
                bg={neverBg}
                color={neverColor}
                borderColor={neverBorder}
                _hover={{ bg: neverBgHover }}
                _active={{ bg: neverBgHover }}
                onClick={onNever}
                isLoading={isNeverBusy}
                isDisabled={isNeverBusy}
              >
                Never
              </Button>
              <Button
                {...sharedButtonProps}
                flex={{ base: 1, sm: 'initial' }}
                bg={notNowBg}
                color={notNowColor}
                borderColor={notNowBorder}
                _hover={{ bg: notNowBgHover }}
                _active={{ bg: notNowBgHover }}
                onClick={onNotNow}
                isDisabled={isNeverBusy}
              >
                Not now
              </Button>
            </HStack>
            <Button
              ref={supportButtonRef}
              {...sharedButtonProps}
              w={{ base: 'full', sm: 'auto' }}
              bg={supportBg}
              color={supportColor}
              borderColor={supportBorder}
              _hover={{ bg: supportBgHover }}
              _active={{ bg: supportBgHover }}
              onClick={onSupport}
              isDisabled={isNeverBusy}
            >
              Support 10 Teams
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
